import React, { useEffect, useRef, useState } from "react";
import { $crud } from "../helper/CrudFactory";

const SCRIPT_SRC = "https://cdn.jsdelivr.net/npm/friendly-challenge@0.9.19/widget.min.js";

function loadFriendlyCaptchaScript() {
  if (window.friendlyChallenge && window.friendlyChallenge.WidgetInstance) {
    return Promise.resolve();
  }
  const existing = document.querySelector("script[data-kjuup-friendlycaptcha]");
  if (existing) {
    return new Promise((resolve, reject) => {
      if (window.friendlyChallenge && window.friendlyChallenge.WidgetInstance) {
        resolve();
        return;
      }
      existing.addEventListener("load", () => resolve(), { once: true });
      existing.addEventListener("error", () => reject(new Error("Friendly Captcha failed to load")), { once: true });
    });
  }
  return new Promise((resolve, reject) => {
    const script = document.createElement("script");
    script.src = SCRIPT_SRC;
    script.async = true;
    script.defer = true;
    script.dataset.kjuupFriendlycaptcha = "1";
    script.onload = () => resolve();
    script.onerror = () => reject(new Error("Friendly Captcha failed to load"));
    document.head.appendChild(script);
  });
}

export default function RegisterCaptcha({ language, onTokenChange }) {
  const containerRef = useRef(null);
  const widgetRef = useRef(null);
  const onTokenChangeRef = useRef(onTokenChange);
  const [siteKey, setSiteKey] = useState(null);
  const [puzzleEndpoint, setPuzzleEndpoint] = useState(null);
  const [enabled, setEnabled] = useState(false);

  onTokenChangeRef.current = onTokenChange;

  useEffect(() => {
    let cancelled = false;
    $crud
      .get("/auth/registerCaptcha")
      .then((data) => {
        if (cancelled) return;
        if (data && data.enabled && data.siteKey) {
          setEnabled(true);
          setSiteKey(data.siteKey);
          setPuzzleEndpoint(
            data.puzzleEndpoint && data.puzzleEndpoint.indexOf("eu-api.friendlycaptcha.eu") === -1
              ? data.puzzleEndpoint
              : "https://api.friendlycaptcha.com/api/v1/puzzle"
          );
          onTokenChangeRef.current({ enabled: true, token: "" });
        } else {
          setEnabled(false);
          setSiteKey(null);
          onTokenChangeRef.current({ enabled: false, token: "" });
        }
      })
      .catch(() => {
        if (cancelled) return;
        setEnabled(false);
        onTokenChangeRef.current({ enabled: false, token: "" });
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!enabled || !siteKey) return undefined;
    let cancelled = false;

    loadFriendlyCaptchaScript()
      .then(() => {
        if (cancelled || !containerRef.current || !window.friendlyChallenge) return;
        const el = containerRef.current;
        el.setAttribute("data-sitekey", siteKey);
        if (puzzleEndpoint) {
          el.setAttribute("data-puzzle-endpoint", puzzleEndpoint);
        }
        // Add the class after the CDN script has run its autobind pass.
        // widget.min.js auto-inits every `.frc-captcha` with no options; if
        // that happens before data-sitekey is set, it shows "sitekey not set".
        el.classList.add("frc-captcha");

        if (widgetRef.current && typeof widgetRef.current.reset === "function") {
          widgetRef.current.loadLanguage((language || "en").toLowerCase());
          widgetRef.current.reset();
          return;
        }

        widgetRef.current = new window.friendlyChallenge.WidgetInstance(el, {
          sitekey: siteKey,
          language: (language || "en").toLowerCase(),
          puzzleEndpoint,
          startMode: "auto",
          doneCallback: (solution) => onTokenChangeRef.current({ enabled: true, token: solution }),
          errorCallback: () => onTokenChangeRef.current({ enabled: true, token: "" }),
        });
      })
      .catch(() => {
        onTokenChangeRef.current({ enabled: true, token: "" });
      });

    return () => {
      cancelled = true;
      if (widgetRef.current && typeof widgetRef.current.reset === "function") {
        widgetRef.current.reset();
      }
    };
  }, [enabled, siteKey, puzzleEndpoint, language]);

  if (!enabled || !siteKey) return null;

  return (
    <div
      className="register-captcha"
      ref={containerRef}
      data-sitekey={siteKey}
      data-puzzle-endpoint={puzzleEndpoint || undefined}
      data-lang={(language || "en").toLowerCase()}
    />
  );
}
